/**
 * Environment Configuration Management
 * 
 * Centralized configuration management with type safety,
 * validation, and environment-specific settings.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import { z } from 'zod';

/**
 * Environment types
 */
export type Environment = 'development' | 'test' | 'staging' | 'production';

/**
 * Configuration schema validation
 */
const ConfigSchema = z.object({
  // Environment
  NODE_ENV: z.enum(['development', 'test', 'staging', 'production']).default('development'),
  
  // Application
  APP_NAME: z.string().default('Personal Website'),
  APP_VERSION: z.string().default('1.0.0'),
  APP_URL: z.string().url().optional(),
  
  // API Configuration
  API_TIMEOUT: z.number().positive().default(10000),
  API_RETRY_ATTEMPTS: z.number().positive().default(3),
  API_RETRY_DELAY: z.number().positive().default(1000),
  
  // Supabase Configuration
  VITE_SUPABASE_URL: z.string().url().optional(),
  VITE_SUPABASE_ANON_KEY: z.string().optional(),
  
  // Features
  ENABLE_ANALYTICS: z.boolean().default(false),
  ENABLE_REAL_TIME: z.boolean().default(true),
  ENABLE_MOCK_DATA: z.boolean().default(true),
  
  // Logging
  LOG_LEVEL: z.enum(['error', 'warn', 'info', 'debug']).default('debug'),
  
  // Performance
  DEBOUNCE_DELAY: z.number().positive().default(300),
  CACHE_TTL: z.number().positive().default(300000), // 5 minutes
  
  // UI
  ANIMATION_DURATION: z.number().positive().default(300),
  TYPING_SPEED: z.number().positive().default(100),
  
  // Pagination
  DEFAULT_PAGE_SIZE: z.number().positive().default(10),
  MAX_PAGE_SIZE: z.number().positive().default(100),
});

/**
 * Configuration type derived from schema
 */
export type Config = z.infer<typeof ConfigSchema>;

/**
 * Environment Configuration Service
 */
class EnvironmentService {
  private config: Config;
  private isInitialized = false;

  constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
    this.isInitialized = true;
  }

  /**
   * Load configuration from environment variables
   */
  private loadConfig(): Config {
    // Get the environment mode
    const mode = import.meta.env.MODE || 'development';
    
    const rawConfig = {
      // Environment
      NODE_ENV: mode,
      
      // Application
      APP_NAME: import.meta.env.VITE_APP_NAME,
      APP_VERSION: import.meta.env.VITE_APP_VERSION,
      APP_URL: import.meta.env.VITE_APP_URL,
      
      // API Configuration
      API_TIMEOUT: this.parseNumber(import.meta.env.VITE_API_TIMEOUT),
      API_RETRY_ATTEMPTS: this.parseNumber(import.meta.env.VITE_API_RETRY_ATTEMPTS),
      API_RETRY_DELAY: this.parseNumber(import.meta.env.VITE_API_RETRY_DELAY),
      
      // Supabase Configuration
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
      
      // Features
      ENABLE_ANALYTICS: this.parseBoolean(import.meta.env.VITE_ENABLE_ANALYTICS),
      ENABLE_REAL_TIME: this.parseBoolean(import.meta.env.VITE_ENABLE_REAL_TIME),
      ENABLE_MOCK_DATA: this.parseBoolean(import.meta.env.VITE_ENABLE_MOCK_DATA),
      
      // Logging - use environment-aware default
      LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL || (mode === 'production' ? 'warn' : 'debug'),
      
      // Performance
      DEBOUNCE_DELAY: this.parseNumber(import.meta.env.VITE_DEBOUNCE_DELAY),
      CACHE_TTL: this.parseNumber(import.meta.env.VITE_CACHE_TTL),
      
      // UI
      ANIMATION_DURATION: this.parseNumber(import.meta.env.VITE_ANIMATION_DURATION),
      TYPING_SPEED: this.parseNumber(import.meta.env.VITE_TYPING_SPEED),
      
      // Pagination
      DEFAULT_PAGE_SIZE: this.parseNumber(import.meta.env.VITE_DEFAULT_PAGE_SIZE),
      MAX_PAGE_SIZE: this.parseNumber(import.meta.env.VITE_MAX_PAGE_SIZE),
    };

    return ConfigSchema.parse(rawConfig);
  }

  /**
   * Parse number from string
   */
  private parseNumber(value: string | undefined): number | undefined {
    if (!value) {return undefined;}
    const parsed = Number(value);
    return isNaN(parsed) ? undefined : parsed;
  }

  /**
   * Parse boolean from string
   */
  private parseBoolean(value: string | undefined): boolean | undefined {
    if (!value) {return undefined;}
    return value.toLowerCase() === 'true';
  }

  /**
   * Validate configuration
   */
  private validateConfig(): void {
    try {
      ConfigSchema.parse(this.config);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const issues = error.issues.map(issue => 
          `${issue.path.join('.')}: ${issue.message}`
        ).join('\n');
        throw new Error(`Configuration validation failed:\n${issues}`);
      }
      throw error;
    }
  }

  /**
   * Get configuration value
   */
  public get<K extends keyof Config>(key: K): Config[K] {
    if (!this.isInitialized) {
      throw new Error('Environment service not initialized');
    }
    return this.config[key];
  }

  /**
   * Get all configuration
   */
  public getAll(): Readonly<Config> {
    return Object.freeze({ ...this.config });
  }

  /**
   * Check if running in development
   */
  public isDevelopment(): boolean {
    return this.config.NODE_ENV === 'development';
  }

  /**
   * Check if running in production
   */
  public isProduction(): boolean {
    return this.config.NODE_ENV === 'production';
  }

  /**
   * Check if running in test
   */
  public isTest(): boolean {
    return this.config.NODE_ENV === 'test';
  }

  /**
   * Check if feature is enabled
   */
  public isFeatureEnabled(feature: keyof Pick<Config, 'ENABLE_ANALYTICS' | 'ENABLE_REAL_TIME' | 'ENABLE_MOCK_DATA'>): boolean {
    return this.config[feature];
  }

  /**
   * Get Supabase configuration
   */
  public getSupabaseConfig(): { url?: string; anonKey?: string; isConfigured: boolean } {
    const url = this.config.VITE_SUPABASE_URL;
    const anonKey = this.config.VITE_SUPABASE_ANON_KEY;
    
    return {
      url,
      anonKey,
      isConfigured: Boolean(url && anonKey),
    };
  }

  /**
   * Get API configuration
   */
  public getApiConfig(): Pick<Config, 'API_TIMEOUT' | 'API_RETRY_ATTEMPTS' | 'API_RETRY_DELAY'> {
    return {
      API_TIMEOUT: this.config.API_TIMEOUT,
      API_RETRY_ATTEMPTS: this.config.API_RETRY_ATTEMPTS,
      API_RETRY_DELAY: this.config.API_RETRY_DELAY,
    };
  }
}

// Export singleton instance
export const environmentService = new EnvironmentService();

// Re-export for convenience
export const config = environmentService; 